import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import type { PackKey } from '../../domain/policies/policyPacks';

const POLICY_EDITOR_STEPS = ['Choose Pack', 'Choose Policy', 'Configure', 'Review & Create'] as const;

export interface PackPolicyCreateDialogProps {
  open: boolean;
  onClose: () => void;
  editingPolicy: any | null;

  editorStep: number;
  setEditorStep: React.Dispatch<React.SetStateAction<number>>;

  selectedPolicyPack: PackKey | null;
  selectionReady: boolean;
  scopeReady: boolean;

  saving: boolean;
  canSavePolicy: boolean;
  primarySaveLabel: string;
  onSave: () => void;

  children?: React.ReactNode;
}

const PackPolicyCreateDialog: React.FC<PackPolicyCreateDialogProps> = ({
  open,
  onClose,
  editingPolicy,
  editorStep,
  setEditorStep,
  selectedPolicyPack,
  selectionReady,
  scopeReady,
  saving,
  canSavePolicy,
  primarySaveLabel,
  onSave,
  children,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Create Policy From Pack'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Stepper activeStep={editorStep} sx={{ mb: 3 }}>
            {POLICY_EDITOR_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {children}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {editorStep > 0 && (
          <Button onClick={() => setEditorStep((prev) => Math.max(0, prev - 1))} disabled={saving}>
            Back
          </Button>
        )}
        {editorStep < 3 ? (
          <Button
            variant="contained"
            onClick={() => {
              if (editorStep === 0 && selectedPolicyPack) {
                setEditorStep(1);
                return;
              }
              if (editorStep === 1 && selectionReady) {
                setEditorStep(2);
                return;
              }
              if (editorStep === 2 && scopeReady) {
                setEditorStep(3);
              }
            }}
            disabled={
              saving ||
              (editorStep === 0 && !selectedPolicyPack) ||
              (editorStep === 1 && !selectionReady) ||
              (editorStep === 2 && !scopeReady)
            }
          >
            {editorStep === 0 ? 'Next: Choose Policy' : editorStep === 1 ? 'Next: Configure' : 'Next: Review'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!canSavePolicy}
          >
            {saving ? 'Working…' : primarySaveLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PackPolicyCreateDialog;
